/**
 * Lead Tracking Hook
 * Captures UTM params, visitor ID, and submits leads to database
 * Enhanced with behavioral engagement scoring
 */

import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getStoredClickIds } from '@/lib/clickIdTracking';
import { trackHighEngagement } from '@/lib/retargetingEvents';

// UTM parameter keys we track
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

// Storage keys
const VISITOR_ID_KEY = 'solar_visitor_id';
const UTM_DATA_KEY = 'solar_utm_data';
const VISIT_COUNT_KEY = 'solar_visit_count';
const FIRST_VISIT_KEY = 'solar_first_visit';

export interface UTMData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  landing_page?: string;
}

export interface LeadData {
  phone_number: string;
  first_name?: string;
  email?: string;
  zip_code?: string;
  city?: string;
  street_address?: string;
  utility_provider?: string;
  monthly_bill?: number;
  projected_savings?: number;
  sms_opt_in?: boolean;
  form_source: 'hero_form' | 'savings_estimate_funnel' | 'phone_modal' | 'exit_popup' | 'booking_calendar' | 'get_quote_landing' | 'sms_opt_in_page' | 'tesla_page' | 'commercial_page';
}

export interface LeadSubmissionResult {
  success: boolean;
  lead_id?: string;
  is_duplicate?: boolean;
  error?: string;
}

/**
 * Generate a unique visitor ID
 */
const generateVisitorId = (): string => {
  return 'v_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
};

/**
 * Get or create visitor ID from localStorage
 */
const getVisitorId = (): string => {
  if (typeof window === 'undefined') return '';
  
  let visitorId = localStorage.getItem(VISITOR_ID_KEY);
  if (!visitorId) {
    visitorId = generateVisitorId();
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
    localStorage.setItem(FIRST_VISIT_KEY, new Date().toISOString());
  }
  return visitorId;
};

/**
 * Get visit count and increment
 */
const getAndIncrementVisitCount = (): { count: number; isReturn: boolean } => {
  if (typeof window === 'undefined') return { count: 1, isReturn: false };
  
  const currentCount = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
  const newCount = currentCount + 1;
  
  // Only increment once per session
  const sessionKey = 'solar_session_counted';
  if (!sessionStorage.getItem(sessionKey)) {
    localStorage.setItem(VISIT_COUNT_KEY, newCount.toString());
    sessionStorage.setItem(sessionKey, 'true');
  }
  
  return {
    count: newCount,
    isReturn: currentCount > 0,
  };
};

/**
 * Capture UTM parameters from URL
 */
const captureUTMParams = (): UTMData => {
  if (typeof window === 'undefined') return {};
  
  const urlParams = new URLSearchParams(window.location.search);
  const utmData: UTMData = {};
  
  // Capture UTM params
  UTM_PARAMS.forEach((param) => {
    const value = urlParams.get(param);
    if (value) {
      utmData[param] = value;
    }
  });
  
  // Capture referrer (only on first visit or if not set)
  const storedData = getStoredUTMData();
  if (!storedData.referrer && document.referrer && !document.referrer.includes(window.location.hostname)) {
    utmData.referrer = document.referrer;
  }
  
  // Capture landing page (only on first visit)
  if (!storedData.landing_page) {
    utmData.landing_page = window.location.pathname + window.location.search;
  }
  
  return utmData;
};

/**
 * Get stored UTM data from localStorage
 */
const getStoredUTMData = (): UTMData => {
  if (typeof window === 'undefined') return {};
  
  try {
    const stored = localStorage.getItem(UTM_DATA_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Merge and store UTM data (first touch attribution)
 */
const mergeAndStoreUTMData = (newData: UTMData): UTMData => {
  if (typeof window === 'undefined') return newData;
  
  const existingData = getStoredUTMData();
  
  // First-touch attribution: only update if not already set
  const mergedData: UTMData = {
    ...newData,
    ...existingData, // Existing data takes precedence (first touch)
  };
  
  // But always keep the most recent landing page for context
  if (newData.landing_page) {
    mergedData.landing_page = newData.landing_page;
  }
  
  localStorage.setItem(UTM_DATA_KEY, JSON.stringify(mergedData));
  return mergedData;
};

/**
 * Sanitize and truncate string input to prevent injection attacks
 */
const sanitizeInput = (value: string | undefined, maxLength: number): string | null => {
  if (!value) return null;
  // Trim whitespace and truncate to max length
  return value.trim().slice(0, maxLength) || null;
};

/**
 * Validate phone number format (E.164 for US: +1XXXXXXXXXX)
 */
const validatePhoneNumber = (phone: string): boolean => {
  return /^\+1[0-9]{10}$/.test(phone);
};

/**
 * Validate email format
 */
const validateEmail = (email: string | undefined): boolean => {
  if (!email) return true; // Optional field
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
};

/**
 * Validate zip code format (5 digits)
 */
const validateZipCode = (zip: string | undefined): boolean => {
  if (!zip) return true; // Optional field
  return /^[0-9]{5}$/.test(zip);
};

/**
 * Valid form source values
 */
const VALID_FORM_SOURCES = ['hero_form', 'savings_estimate_funnel', 'phone_modal', 'exit_popup', 'booking_calendar', 'get_quote_landing', 'sms_opt_in_page', 'tesla_page', 'commercial_page'] as const;

/**
 * Submit lead via rate-limited edge function
 */
const submitLead = async (leadData: LeadData): Promise<LeadSubmissionResult> => {
  try {
    // Client-side validation (edge function also validates server-side)
    if (!validatePhoneNumber(leadData.phone_number)) {
      return { success: false, error: 'Invalid phone number format' };
    }
    
    if (!validateEmail(leadData.email)) {
      return { success: false, error: 'Invalid email format' };
    }
    
    if (!validateZipCode(leadData.zip_code)) {
      return { success: false, error: 'Invalid zip code format' };
    }
    
    if (!VALID_FORM_SOURCES.includes(leadData.form_source)) {
      return { success: false, error: 'Invalid form source' };
    }
    
    const visitorId = getVisitorId();
    const { count: visitCount, isReturn } = getAndIncrementVisitCount();
    const utmData = getStoredUTMData();
    const clickIds = getStoredClickIds();
    
    // Get session depth from behavioral data
    let pagesViewed = 0;
    let timeOnSiteSeconds = 0;
    let maxScrollDepth = 0;
    try {
      const behaviorData = JSON.parse(localStorage.getItem('solar_behavior_data') || '{}');
      pagesViewed = behaviorData.totalPageViews || 0;
      timeOnSiteSeconds = behaviorData.totalTimeOnSite || 0;
      maxScrollDepth = behaviorData.maxScrollDepth || 0;
    } catch { /* silent */ }
    
    // Submit via rate-limited edge function instead of direct DB insert
    const { data, error } = await supabase.functions.invoke('submit-lead', {
      body: {
        phone_number: leadData.phone_number,
        first_name: leadData.first_name,
        email: leadData.email,
        zip_code: leadData.zip_code,
        city: leadData.city,
        street_address: leadData.street_address,
        utility_provider: leadData.utility_provider,
        monthly_bill: leadData.monthly_bill,
        projected_savings: leadData.projected_savings,
        sms_opt_in: leadData.sms_opt_in ?? false,
        form_source: leadData.form_source,
        visitor_id: visitorId,
        is_return_visitor: isReturn,
        visit_count: visitCount,
        utm_source: utmData.utm_source,
        utm_medium: utmData.utm_medium,
        utm_campaign: utmData.utm_campaign,
        utm_term: utmData.utm_term,
        utm_content: utmData.utm_content,
        referrer: utmData.referrer,
        landing_page: utmData.landing_page,
        gclid: clickIds.gclid,
        fbclid: clickIds.fbclid,
        msclkid: clickIds.msclkid,
        pages_viewed: pagesViewed,
        time_on_site_seconds: timeOnSiteSeconds,
        max_scroll_depth: maxScrollDepth,
        current_page: typeof window !== 'undefined' ? window.location.pathname : undefined,
      },
    });
    
    if (error) {
      console.error('[Lead Tracking] Edge function error:', error);
      return { success: false, error: error.message };
    }
    
    if (!data?.success) {
      console.error('[Lead Tracking] Submission failed:', data?.error);
      return { success: false, error: data?.error || 'Submission failed' };
    }
    
    console.log('[Lead Tracking] Lead submitted:', {
      lead_id: data.lead_id,
      is_duplicate: data.is_duplicate,
      form_source: leadData.form_source,
    });

    // Persist last lead id so /booking-confirmation can attribute the booking for reminders
    try {
      if (data.lead_id && typeof window !== 'undefined') {
        localStorage.setItem('ceag_last_lead_id', data.lead_id);
        localStorage.setItem('ceag_last_lead_at', new Date().toISOString());
      }
    } catch { /* silent */ }
    
    // Fire retargeting event for high engagement leads
    try {
      const { calculateEngagementScore } = await import('@/hooks/useBehavioralTracking');
      const { score } = calculateEngagementScore();
      trackHighEngagement(score);
    } catch { /* silent */ }
    
    return {
      success: true,
      lead_id: data.lead_id,
      is_duplicate: data.is_duplicate,
    };
  } catch (error) {
    console.error('[Lead Tracking] Unexpected error:', error);
    return { success: false, error: 'Unexpected error occurred' };
  }
};

/**
 * Hook for lead tracking
 */
export const useLeadTracking = () => {
  // Capture UTM params on mount
  useEffect(() => {
    const newUTMData = captureUTMParams();
    if (Object.keys(newUTMData).length > 0) {
      mergeAndStoreUTMData(newUTMData);
    }
    
    // Initialize visitor ID
    getVisitorId();
    
    // Track visit
    getAndIncrementVisitCount();
  }, []);
  
  const trackLead = useCallback(async (data: LeadData): Promise<LeadSubmissionResult> => {
    return submitLead(data);
  }, []);
  
  const getVisitorInfo = useCallback(() => {
    return {
      visitorId: getVisitorId(),
      visitCount: parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '1', 10),
      isReturnVisitor: parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10) > 1,
      firstVisit: localStorage.getItem(FIRST_VISIT_KEY),
      utmData: getStoredUTMData(),
    };
  }, []);
  
  return {
    trackLead,
    getVisitorInfo,
  };
};

// Export standalone functions for use in components without the hook
export { submitLead, getStoredUTMData, getVisitorId };
